<template>
  <div v-click-outside="hideAutoComplete">
    <div class="basic-text-input-container">
      <Field
        v-slot="{ field }"
        v-model="mutatedValue"
        validateOnInput
        :label="label"
        :name="name"
        :rules="validation"
        @update:model-value="onInput()"
      >
        <label 
          class="bordered-bottom"
          :class="{'autocompleted': valueHasBeenAutocompleted}"
        >
          <span v-if="displayLabel">{{ label }} {{ validation.split('|').includes('required') ? '*' : '' }}</span>
          <input
            :ref="name"
            type="text"
            :model-value="field.value"
            v-bind="field"
            :disabled="disabledInteractionComputed || valueHasBeenAutocompleted"
            :placeholder="`${placeholder} ${!displayLabel && validation.split('|').includes('required') ? '*' : ''}`"
            :maxlength="mutatedMaxLength"
            :readonly="isReadOnly"
            :autocomplete="autocompletePropsValue"
            @keyup="autocompleteOnKeyUp"
          >
          <img
            v-if="![null, undefined, ''].includes(mutatedValue) && !isReadOnly"
            class="form-generator-clear-button"
            src="@/assets/icons/cancel.svg"
            alt="clear input"
            @click="clearInput"
          >
        </label>
        <ErrorMessage
          class="error-msg"
          :name="name"
        />
      </Field>
    </div>
    <div
      v-if="autocomplete_.list && autocomplete_.active"
      id="autocompleteList"
    >
      <ul @blur="autocomplete_.active = false">
        <li
          v-for="item in autocomplete_.list"
          :key="item.id"
          class="autocomplete-item"
          @click="selectAutocomplete(item)"
        >
          <span v-if="typeof item === 'string'">{{ item }}</span>
          <span v-else-if="item.formatedValue">{{ item.formatedValue }}</span>
          <span v-else>{{ getValueOfAutocompleteItem(item) }}</span>
        </li>
      </ul>
    </div>
  </div>
</template>
<script>
import vClickOutside from 'click-outside-vue3'
import { Field, ErrorMessage } from 'vee-validate'
import { autoCompleteService } from '@/services/autocomplete.service'
import _ from 'lodash'
import { EventBus } from '@/event-bus'
import { mapState } from 'pinia'
import { appConfigurationStore } from '@/stores/appConfigurationStore.js'

/**
 * Props:
 * placeholder: placeholder that'll be set inside the textarea (string)
 * value: text that'll be set inside the input, if null, then the placeholder we'll be displayed (string)
 * name: input's name (string)
 */
export default {
  name: 'BasicTextInput',
  components: {
    Field,
    ErrorMessage
  },
  expose: ['hideAutoComplete', 'clearInput'],
  directives: {
    clickOutside: vClickOutside.directive
  },
  props: {
    label: String,
    validation: [String, Boolean],
    placeholder: String,
    max: [String, Number],
    name: String,
    defaultValue: [String, Array, Number],
    disabledInteraction: Boolean,
    isReadOnly: {
      type: Boolean,
      default: false
    },
    setAutocomplete: {
      type: Function,
      default: null
    },
    getValidationValue: {
      type: Function,
      default: null
    },
    getChildByName: {
      type: Function,
      default: null
    },
    displayLabel: {
      type: Boolean,
      default: false
    },
    autocomplete: {
      type: Object,
      default: function () {
        return {
          enable: false,
          type: null,
          field: null,
          min_length: 3,
          active: false,
          list: []
        }
      }
    }
  },
  emits: ['addData'],
  data() {
    return {
      mutatedValue: this.defaultValue,
      autocomplete_: {},
      valueHasBeenAutocompleted: false,
      autocompletePropsValue: 'off'
    }
  },
  computed: {
    ...mapState(appConfigurationStore, ['appConfiguration']),
    mutatedMaxLength() {
      return this.getValidationValue(this.validation, 'max')
    },
    disabledInteractionComputed() {
      if (this.disabledInteraction) {
        return this.defaultValue.length !== 0
      }
      return false
    }
  },
  created() {
    let chrome = /chrom(e|ium)/.test(navigator.userAgent.toLowerCase())
    if(chrome) this.autocompletePropsValue = 'NoAutocompletion'
  },
  mounted() {
    this.mutatedValue
      ? this.$emit('addData', { value: this.mutatedValue, name: this.name })
      : this.$emit('addData', { value: '', name: this.name })
    this.autocomplete_ = this.autocomplete

    //Disable autocomplete for datacapture
    this.autocomplete_.enable = false
    
    EventBus.on('changeValue' + this.name, value => {
      if(value == '') {
        this.valueHasBeenAutocompleted = false
      } else {
        this.valueHasBeenAutocompleted = true
      }
      this.mutatedValue = value
    })

    EventBus.on('changeAutocomplete' + this.name, values => {
      this.autocomplete_ = {
        enable: false,
        type: null,
        field: null,
        min_length: 3,
        active: true,
        list: values
      }
    })
  },
  beforeUnmount() {
    EventBus.off('changeValue' + this.name)
    EventBus.off('changeAutocomplete' + this.name)
  },
  methods: {
    onInput() {
      this.mutatedValue = this.mutatedValue ? this.mutatedValue.replaceAll(/[;:/\\[\]{}<>=%+]/g, '') : this.mutatedValue

      this.$emit('addData', { value: this.mutatedValue, name: this.name })
      if (this.valueHasBeenAutocompleted) {
        let nameFormated = ''
        if (this.name == 'postcode') {
          nameFormated = 'postalcode'
        } else {
          nameFormated = this.name
        }
        this.valueHasBeenAutocompleted = false
      }
    },
    /* event for autocomplete field */
    autocompleteOnKeyUp: _.debounce(function () {
      /* if autocomplete enabled on field */
      if (this.autocomplete_.enable && this.mutatedValue) {
        /* check if field length is ok for launch autocomplete */
        if (
          this.mutatedValue &&
          this.mutatedValue.length < Math.min(this.autocomplete.min_length, parseInt(this.mutatedMaxLength))
        ) {
          this.autocomplete_['list'] = []
          this.autocomplete_.active = false
          return
        }
        let cityInput = this.getChildByName('city')
        let postalCodeInput = this.getChildByName('postcode')
        let distribution = this.getChildByName('distribution')
        let countryInput = this.getChildByName('country') || this.getChildByName('id_country')
        switch (this.autocomplete.type) {
          case 'address':
            if (cityInput || postalCodeInput) {
              autoCompleteService
                .autoCompleteStreet(
                  this.mutatedValue,
                  cityInput ?? '',
                  postalCodeInput ?? '',
                  distribution ?? '',
                  countryInput
                ).then(response => {
                  if (response.length > 0) {
                    if (this.autocomplete.field === this.name) {
                      this.autocomplete_.active = true
                      this.autocomplete_['list'] = response
                    } else {
                      this.autocomplete_.active = false
                      this.autocomplete_['list'] = []
                    }
                  }
                })
                .catch(() => {
                  this.autocomplete_['list'] = []
                  this.autocomplete_.active = false
                })
            }
            break
          case 'postalcode':
            autoCompleteService
              .autoCompletePostalCode(this.mutatedValue, countryInput)
              .then(response => {
                if (response.length > 0) {
                  /* Call parent to set list in other field */
                  this.setAutocomplete(response, 'city')
                } else {
                  this.setAutocomplete(false, this.autocomplete.field)
                  this.autocomplete_.active = false
                  this.autocomplete_['list'] = []
                }
              })
              .catch(() => {
                this.autocomplete_.active = false
                this.autocomplete_['list'] = []
              })
            break
          case 'city':
            autoCompleteService
              .autoCompleteCity(this.mutatedValue, countryInput)
              .then(response => {
                if (response.length > 0) {
                  /* Call parent to set list in other field */
                  this.setAutocomplete(response, 'city')
                } else {
                  this.setAutocomplete(false, this.autocomplete.field)
                  this.autocomplete_.active = false
                  this.autocomplete_['list'] = []
                }
              })
              .catch(() => {
                this.autocomplete_.active = false
                this.autocomplete_['list'] = []
              })
            break
          default:
        }
      }
    }, 500),
    getValueOfAutocompleteItem(item) {
      let formatedString = ''
      Object.values(item).forEach(value => {
        if (formatedString) {
          formatedString += ' '
        }
        formatedString += value
      })
      return formatedString
    },
    selectAutocomplete(item) {
      this.$refs[this.name].focus()
      if (typeof item === 'string') {
        this.mutatedValue = item
        this.valueHasBeenAutocompleted = true
        this.$emit('addData', { value: item, name: this.name })
        this.autocomplete_.active = false
      } else {
        Object.keys(item).forEach(field => {
          let formatedField = ''
          if (field == 'postalcode') {
            formatedField = 'postcode'
          } else {
            formatedField = field
          }

          if (field === this.name) {
            this.mutatedValue = item[field]
          } else {
            EventBus.emit('changeValue' + formatedField, item[field])
          }
          
          this.valueHasBeenAutocompleted = true
          this.$emit('addData', { value: item[field], name: formatedField })
          this.autocomplete_.active = false
          EventBus.emit('changeValuecountry', 'x') // No need to autocomplete with a value here
        })
      }
    },
    hideAutoComplete(e) {
      if (e.srcElement.className !== 'autocomplete-item' && this.autocomplete_.active == true) {
        let countryInput = this.getChildByName('country') || this.getChildByName('id_country') || 'FR'
        if(this.autocomplete_.active && countryInput == 'FR' && (this.name == 'city' || this.name == 'postcode' || this.name == 'postalcode')) {
          EventBus.emit('changeValue' + 'city', '')
          EventBus.emit('changeValue' + 'postcode', '')
          EventBus.emit('changeValue' + 'postalcode', '')
          EventBus.emit('changeValue' + 'region', '')
        }
        this.autocomplete_.active = false
      }
    },
    clearInput() {
      this.mutatedValue = ''
      this.$emit('addData', { value: this.mutatedValue, name: this.name })
      if(!this.disabledInteractionComputed) {
        if(this.valueHasBeenAutocompleted) {
          if(this.name == 'city') {
            EventBus.emit('changeValue' + 'postcode', '')
            EventBus.emit('changeValue' + 'postalcode', '')
            EventBus.emit('changeValue' + 'region', '')
            EventBus.emit('changeValue' + 'line1', '')
            EventBus.emit('changeValue' + 'line2', '')
            EventBus.emit('changeValue' + 'line3', '')
            EventBus.emit('changeValue' + 'distribution', '')
            EventBus.emit('changeValue' + 'country', '')
          } else if (this.name == 'postcode' || this.name == 'postalcode') {
            EventBus.emit('changeValue' + 'city', '')
            EventBus.emit('changeValue' + 'region', '')
            EventBus.emit('changeValue' + 'line1', '')
            EventBus.emit('changeValue' + 'line2', '')
            EventBus.emit('changeValue' + 'line3', '')
            EventBus.emit('changeValue' + 'distribution', '')
            EventBus.emit('changeValue' + 'country', '')
          }
        }
        this.valueHasBeenAutocompleted = false
      }
    },
    isCapencyEnable() {
      return this.$appConfigurationStore.appConfiguration?.modules?.general?.autocomplete?.validation?.enabled && this.$appConfigurationStore.appConfiguration?.modules?.general?.autocomplete?.validation?.providerName == 'capency'
    }
  }
}
</script>
<style lang="scss" scoped>
  .basic-text-input-container {
    margin: 10px 0;

    label {
      width: 100%;
      display: flex;
      flex-direction: row;
      justify-content: space-between;
      white-space: nowrap;
      align-items: center;
      padding-left: 0.5rem;

      span {
        font-weight: bolder;
        margin-right: 1rem;
      }

      input {
        border: none;
        flex-grow: 1;
      }

    }

    label.autocompleted {
      border-bottom: 1px #777 dotted;
    }
  }

  #autocompleteList {
    background: #fefefe;
    border: 1px solid #ccc;
    z-index: 4;
    left: 0;
    right: 0;
  }

  #autocompleteList li {
    padding: 10px;
    border-bottom: 1px solid #ccc;
    cursor: pointer;
  }

  #autocompleteList li:last-child {
    border: none;
  }
</style>
